import { useEffect, useState } from 'react';
import { useI18n } from '../lib/i18n';
import { isIos, isStandalone, useInstall } from '../lib/install';
import { disablePush, enablePush, pushState, type PushState } from '../lib/push';
import { Sheet } from './Sheet';
import { Button } from './ui';

/** iOS share glyph, drawn rather than described: the words for it differ by locale. */
function ShareMark() {
  return (
    <svg viewBox="0 0 24 24" className="inline-block size-4 align-text-bottom" aria-hidden="true">
      <path
        d="M12 3v11M12 3l-3.2 3.2M12 3l3.2 3.2M6 11.5v7A1.5 1.5 0 0 0 7.5 20h9a1.5 1.5 0 0 0 1.5-1.5v-7"
        fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * One line about notifications on this phone, and nothing on a desk.
 *
 * On an iPhone they work only from the home-screen app, so Safari gets the
 * steps behind a button instead of a toggle that cannot work. The steps name
 * iOS's "Open as Web App" switch: with it off, the icon just opens Safari,
 * which is how someone can "install" the portal and see no difference.
 */
export function PhoneSetup() {
  const { t } = useI18n();
  const { state: install, install: prompt } = useInstall();
  const [push, setPush] = useState<PushState | null>(null);
  const [busy, setBusy] = useState(false);
  const [steps, setSteps] = useState(false);

  useEffect(() => { void pushState().then(setPush); }, []);

  if (install === 'desktop' || push === null) return null;

  const run = (action: () => Promise<PushState>) => {
    setBusy(true);
    action().then(setPush).catch(() => undefined).finally(() => setBusy(false));
  };

  const needsHomeScreen = isIos() && !isStandalone();
  let action: React.ReactNode = null;
  let label = t('push.off');
  if (needsHomeScreen) {
    action = <Button size="sm" variant="ghost" onClick={() => setSteps(true)}>{t('push.how')}</Button>;
  } else if (install === 'ready') {
    label = t('install.title');
    action = <Button size="sm" variant="ghost" loading={busy} onClick={() => { setBusy(true); void prompt().finally(() => setBusy(false)); }}>{t('install.button')}</Button>;
  } else if (push === 'blocked') {
    label = t('push.blocked');
  } else if (push === 'on') {
    label = t('push.on');
    action = <Button size="sm" variant="quiet" loading={busy} onClick={() => run(disablePush)}>{t('push.turnOff')}</Button>;
  } else if (push === 'off') {
    action = <Button size="sm" variant="ghost" loading={busy} onClick={() => run(enablePush)}>{t('push.turnOn')}</Button>;
  } else {
    return null;
  }

  return (
    <>
      <div className="flex min-h-14 items-center justify-between gap-3 rounded-xl border border-line px-3.5 py-2">
        <span className="text-sm text-ink-dim">{label}</span>
        {action}
      </div>
      <Sheet open={steps} onClose={() => setSteps(false)} title={t('push.howTitle')}>
        <ol className="space-y-3 text-sm leading-relaxed text-ink-dim">
          {[
            <>{t('install.ios1')} <ShareMark /></>,
            t('install.ios2'),
            t('install.ios3'),
            t('install.ios4'),
          ].map((text, index) => (
            <li key={index} className="flex gap-3">
              <span className="tabular grid size-6 shrink-0 place-items-center rounded-full border border-line text-[0.7rem] text-ink-faint">{index + 1}</span>
              <span className="pt-0.5">{text}</span>
            </li>
          ))}
        </ol>
      </Sheet>
    </>
  );
}
